import React, { forwardRef } from 'react'
import styled from 'styled-components'

import HelpVideoLink from 'components/HelpVideoLink'
import KeyboardAccessible from 'components/KeyboardAccessible'
import Spinner from 'components/Spinner'

import { color } from 'styles/colors'
import { PanelTitle, screenSize } from 'styles/shared'

type Ref = HTMLDivElement

type Props = React.PropsWithChildren<{
  title?: string | null
  titleAreaLeft?: React.ReactNode | null
  titleAreaRight?: React.ReactNode | null
  titleAreaStyle?: string | null
  className?: string
  onClickTitleAreaCallback?: () => void | null
  isLoaded?: boolean
  videoKey?: string | null
}>

const Card = forwardRef<Ref, Props>(({
  title,
  titleAreaLeft,
  titleAreaRight,
  titleAreaStyle,
  children,
  className,
  onClickTitleAreaCallback,
  isLoaded = true,
  videoKey = null
}: Props, ref) => (
  <S.Container ref={ref} className={className}>
    {title && (
      <S.TitleArea onClick={onClickTitleAreaCallback} enabled={!!onClickTitleAreaCallback} customStyle={titleAreaStyle || ''}>
        {titleAreaLeft}
        {videoKey === null
          ? <PanelTitle>{title}</PanelTitle>
          : (
            <S.PanelHeader>
              <S.PanelTitle>
                {title}
              </S.PanelTitle>
              <HelpVideoLink videoKey={videoKey} />
            </S.PanelHeader>
          )
        }
        {titleAreaRight}
      </S.TitleArea>
    )}

    {isLoaded ? children : <Spinner variant="page" />}
  </S.Container>
))

export default Card

const S = {
  Container: styled.div`
    padding: 20px;
    border-radius: 4px;
    background-color: ${color('paneBackground')};

    @media ${screenSize.small} {
      padding: 10px;
    }
  `,
  TitleArea: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['customStyle'].includes(prop)
  })<{ customStyle: string }>`
    display: flex;
    justify-content: left;
    width: 100%;
    text-align: left;

    ${({ customStyle }) => customStyle}
  `,
  PanelTitle: styled(PanelTitle)`
    padding: 3px;
  `,
  PanelHeader: styled.div`
    display: flex;
    align-content: center;
    justify-content: space-between;
  `
}
